/* eslint-disable @typescript-eslint/no-explicit-any */
import { getStepRuleRefIds } from 'store/rule/rule.selector';
import { RemoveRuleAction, InsertRuleAction, SaveRuleAction } from 'store/actionTypes';
import { insertRuleAction, updateRuleAction, removeRuleAction } from './rule.action';
import { RootState } from 'store/store';
import { FullTime, Option } from 'types/common';
import { buildUnixInTz } from 'helpers/datetime.helper';

type ScheduleTimezoneOption = Option & { offset?: string | number };

type UpdateStepScheduleInput = {
  stepIdx: number;
  type: 'start' | 'end';
  dataType: 'date' | 'time' | 'tz';
  hasEndDate: boolean;
  newValues: { date?: Date; time?: { value: number }; tz?: ScheduleTimezoneOption };
  newDateTime: { start: FullTime; end: FullTime };
};

/**
 * Handles the redux side of a date/time/timezone edit in the touchpoint
 * schedule editor. Kept as a thunk so the component doesn't have to decide
 * between per-row vs atomic-timezone dispatches or compute the derived unix
 * values itself.
 *
 * - When the timezone changes, dispatches a single UPDATE_SCHEDULE with
 *   type: 'both' and per-side patches (startFields/endFields) so both rows'
 *   unixes are rebuilt against the new tz in one reducer pass, using each
 *   row's existing wall-clock. The reducer's existence guard ensures we only
 *   update rows that already exist.
 * - Otherwise (date or time edit on a single row), dispatches UPDATE_SCHEDULE
 *   for just the edited row. No-ops when the row isn't fully specified yet.
 */
export function updateStepScheduleThunk({
  stepIdx,
  type,
  dataType,
  hasEndDate,
  newValues,
  newDateTime,
}: UpdateStepScheduleInput) {
  return (dispatch: any) => {
    if (dataType === 'tz' && newValues.tz?.name) {
      const startUnix = buildUnixInTz(newDateTime.start.date, newDateTime.start.time?.value, newValues.tz.name);
      const endUnix = buildUnixInTz(newDateTime.end.date, newDateTime.end.time?.value, newValues.tz.name);

      const timezone = { name: newValues.tz.name, offset: newValues.tz.offset };

      dispatch({
        type: 'UPDATE_SCHEDULE',
        payload: {
          stepIdx,
          type: 'both',
          // Shared fields: timezone applies to both rows
          timezone,
          // Per-side patches: only update existing entries (existence guard)
          startFields: startUnix !== undefined ? { start: startUnix } : undefined,
          endFields: hasEndDate && endUnix !== undefined ? { start: endUnix } : undefined,
        },
      });
      return;
    }

    const isRowFullySpecified = !!newValues.date && newValues.time?.value !== undefined && !!newValues.tz?.name;
    if (!isRowFullySpecified) return;

    // Narrow the type after the existence check
    const tz = newValues.tz;
    const time = newValues.time;
    if (!tz || !time) return;

    const startUnix = buildUnixInTz(newValues.date, time.value, tz.name);

    dispatch({
      type: 'UPDATE_SCHEDULE',
      payload: {
        stepIdx,
        type,
        start: startUnix,
        cronExpression: undefined,
        timezone: {
          name: tz.name,
          offset: tz.offset,
        },
      },
    });
  };
}

export function insertRule(payload: InsertRuleAction['payload']) {
  return (dispatch: any, getState: any) => {
    const teState = getState().te;
    const stepRuleRefIds = getStepRuleRefIds(teState, payload.stepIdx ?? 0);
    const stepPushRefId = teState.journey.steps[payload.stepIdx ?? 0]?.push[0];
    const mainStepRuleRefId =
      payload.type === 'restricted' ? payload.refId : stepRuleRefIds.restricted ?? payload.refId;

    dispatch({ type: 'INSERT_RULE', payload });
    if (stepPushRefId) {
      dispatch({
        type: 'UPDATE_PUSH_DEPENDENCY',
        payload: {
          refId: stepPushRefId,
          type: 'rule',
          value: mainStepRuleRefId,
        },
      });
    }
  };
}

export function removeRulesThunk(payload: RemoveRuleAction['payload']) {
  return (dispatch: any, getState: any) => {
    const teState = getState().te;
    const stepRuleRefIds = getStepRuleRefIds(teState, payload.stepIdx ?? 0);
    const stepPushRefId = teState.journey.steps[payload.stepIdx ?? 0]?.push;

    if (payload.refId === stepRuleRefIds.restricted) {
      stepRuleRefIds.restricted = '';
    }

    if (payload.refId === stepRuleRefIds.preferred) {
      stepRuleRefIds.preferred = '';
    }

    const mainStepRuleRefId = stepRuleRefIds.restricted || stepRuleRefIds.preferred;

    dispatch({ type: 'REMOVE_RULE', payload });
    if (stepPushRefId) {
      dispatch({
        type: 'UPDATE_PUSH_DEPENDENCY',
        payload: {
          refId: stepPushRefId,
          type: 'rule',
          value: mainStepRuleRefId || undefined,
        },
      });
    }
  };
}

export function saveRulesThunk(payload: SaveRuleAction['payload']) {
  return (dispatch: any, getState: any) => {
    const teState: RootState['te'] = getState().te;
    const stepRuleRefIds = getStepRuleRefIds(teState, payload.stepIdx ?? 0);
    const rule = teState.rule.byRefId[stepRuleRefIds[payload.type]];

    //  no rule present in journey && empty/no rule returned from rb => no change
    if (!rule && !payload.refId) {
      return;
    }

    // no rule present && rule returned
    if (!rule && payload.refId) {
      dispatch(
        insertRuleAction({
          refId: payload.refId,
          type: payload.type,
          rulePayload: payload.rulePayload,
          stepIdx: payload.stepIdx,
        }),
      );
    }

    // rule present && was wiped
    if (rule && !payload.refId) {
      // wipe if rule is NOT deletable ...
      if (!rule.isOptional) {
        dispatch(
          updateRuleAction({
            ...payload,
            refId: rule.refId,
          }),
        );
        // ... remove if it is
      } else {
        let wipeDependencies = false;
        for (const [key, value] of Object.entries(stepRuleRefIds)) {
          if (key !== payload.type && !value) wipeDependencies = true;
        }
        dispatch(
          removeRuleAction({
            refId: payload.refId,
            type: payload.type,
            stepIdx: payload.stepIdx,
            wipeDependencies,
          }),
        );
      }
    }

    // rule present && updated
    if (rule && payload.refId) {
      dispatch(updateRuleAction(payload));
    }
  };
}
